import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../components/ui/Button';
import { Dropdown } from 'primereact/dropdown';

import { convertToPx, handleSwitchUnit } from '../../../../../../config/layoutHelper';
import { layoutColor, unitOptions } from '../../../../../../config/layoutConstants';
import DynamicContainerPreview from "./Warning_preview/DynamicContainerPreview";
import LayoutNameController from '../../Custom/Controls/LayoutNameController';
import DynamicContainer from "../../Custom/Controls/DynamicContainer";
import AreaControls from "../../Custom/Controls/AreaControls";
import DesignToolsPanel from '../../Custom/Controls/DesignToolsPanel';
import ToolTutorialModal from '../../Custom/Controls/ToolTutorialModal';
import AreaSelectorBar from '../../Custom/Controls/AreaSelectorBar';

import { useUpdateLayout } from '../../../../../../queries/layouts';
import { toggleDialog } from '../../../../../../redux/Slices/Global/globalSlice';
import { setScreenDetails, setUnit } from '../../../../../../redux/Slices/layoutSlice';
import LayoutConflictWarning from './LayoutConflictWarning';
import PreviewLayoutModal from './PreviewLayoutModal';
import useDesignTools from '../../../../../../hooks/useDesignTools';
import '../../../../../css/designTools.css';

function EditLayout({ isMaximized, activeIndex, setActiveIndex, navItems }) {
    const { t } = useTranslation();
    const dispatch = useDispatch()
    const { id } = useParams();
    const { subLoaders, openDialog } = useSelector((state) => state.global);

    const { selectedLayout, layoutAspectRatio, unit } = useSelector((state) => state.layout);
    const screens_setting = useSelector((state) => state.layout.screens_setting);
    const newAreas = screens_setting.map((area, index) => {
        const idVal = area.id || index + 1;
        let orderVal = area.settings?.live?.order ?? area.order;

        if (!orderVal && !isNaN(parseInt(area.id, 10))) {
            orderVal = area.id;
        }

        if (!orderVal) {
            orderVal = index + 1;
        }

        return {
            ...area,
            id: idVal,
            order: parseInt(orderVal, 10),
            unit: "%"
        };
    }).sort((a, b) => {
        const orderA = parseInt(a.order, 10) || parseInt(a.id, 10) || 9999;
        const orderB = parseInt(b.order, 10) || parseInt(b.id, 10) || 9999;
        return orderA - orderB;
    });

    const { mutate: updateLayout, isLoading } = useUpdateLayout();

    // UI State
    const [sidebarWidth, setSidebarWidth] = useState(384);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    const [currentAspectRatio, setCurrentAspectRatio] = useState(layoutAspectRatio);
    const [isConflictScreens, setIsConflictScreens] = useState(false);
    const [overlappingAreas, setOverlappingAreas] = useState([]);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [oldAspectRatio, setOldAspectRatio] = useState({});
    const [layoutName, setLayoutName] = useState({ isEditing: false, name: selectedLayout.title });
    const [titleError, setTitleError] = useState("");
    const [oldLayout, setOldLayout] = useState(selectedLayout.screens_setting);
    const [newLayout, setNewLayout] = useState([]);
    const [areas, setAreas] = useState(newAreas);
    const scrollableDivRef = useRef(null);
    const [sidebarTab, setSidebarTab] = useState('areas'); // 'areas' or 'tools'
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    // Design Tools Hook
    const {
        toolsState,
        togglePanel,
        setActiveTab,
        updateTool,
        toggleTool,
        setAreaColor,
        setAreaOpacity,
        showToolTutorial,
        hideTutorial,
        resetTools,
        selectedTool,
        showTutorial,
        toggleSidebarPin,
        toggleDarkMode
    } = useDesignTools();

    const isSidebarPinned = toolsState?.sidebarPinned;
    const isDarkMode = toolsState?.darkMode;
    const [isSidebarOpenUnpinned, setIsSidebarOpenUnpinned] = useState(false);

    // Close sidebar when clicking outside if unpinned
    useEffect(() => {
        if (!isSidebarPinned) {
            const handleClickOutside = (e) => {
                const sidebar = document.getElementById('layout-editor-sidebar');
                const strip = document.getElementById('sidebar-strip');
                const header = document.querySelector('.layout-editor-header'); // Add class to header

                if (isSidebarOpenUnpinned && 
                    sidebar && !sidebar.contains(e.target) && 
                    strip && !strip.contains(e.target) && 
                    header && !header.contains(e.target)) { // Avoid closing when clicking Pin button
                    setIsSidebarOpenUnpinned(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        } else {
            // When switching to pinned, ensure full visibility logic resets if needed
            setIsSidebarOpenUnpinned(false); 
        }
    }, [isSidebarPinned, isSidebarOpenUnpinned]);

    // ESC key to close preview modal
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && showPreview) {
                setShowPreview(false);
            }
        };

        window.addEventListener('keydown', handleEscKey);
        return () => window.removeEventListener('keydown', handleEscKey);
    }, [showPreview]);

    const handleSidebarTabClick = (tab) => {
        setSidebarTab(tab);
        if (!isSidebarPinned) {
            setIsSidebarOpenUnpinned(true);
        }
    };

    // Sidebar Resizing Logic
    const startResizing = React.useCallback((mouseDownEvent) => {
        mouseDownEvent.preventDefault();
        mouseDownEvent.stopPropagation(); // Prevent closing sidebar when clicking handle
        setIsResizing(true);
        const startX = mouseDownEvent.clientX;
        const startWidth = sidebarWidth;

        const doDrag = (mouseMoveEvent) => {
             const newWidth = startWidth - (mouseMoveEvent.clientX - startX);
             if (newWidth > 280 && newWidth < 600) {
                 setSidebarWidth(newWidth);
             }
        };

        const stopDrag = () => {
             setIsResizing(false);
             document.removeEventListener('mousemove', doDrag);
             document.removeEventListener('mouseup', stopDrag);
        };

        document.addEventListener('mousemove', doDrag);
        document.addEventListener('mouseup', stopDrag);
    }, [sidebarWidth]);

    // Zoom Logic
    const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 2));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
    const handleResetZoom = () => setZoomLevel(1);
    const toggleCanvasFullscreen = () => setIsCanvasFullscreen(prev => !prev);

    const handleCancel = () => {
        if (unit.value == "%") {
            setAreas(newAreas)
        } else {
            const baseArea = newAreas;
            const areasConverted = convertToPx(baseArea, currentAspectRatio);
            setAreas(areasConverted)
        }
    }

    const handleCloseAttempt = () => {
        if (hasUnsavedChanges) {
            setShowUnsavedWarning(true);
        } else {
            dispatch(toggleDialog("EditLayoutPopUp"));
        }
    }

    const handleDiscardChanges = () => {
        setShowUnsavedWarning(false);
        setHasUnsavedChanges(false);
        dispatch(toggleDialog("EditLayoutPopUp"));
    }

    useEffect(() => {
        setOldAspectRatio(layoutAspectRatio)
        setCurrentAspectRatio(layoutAspectRatio)
        setLayoutName({ isEditing: false, name: `${selectedLayout.title}` });
    }, [layoutAspectRatio]);

    // Track unsaved changes
    useEffect(() => {
        const hasAreaChanges = JSON.stringify(areas) !== JSON.stringify(newAreas);
        const hasNameChange = layoutName.name !== selectedLayout.title;
        setHasUnsavedChanges(hasAreaChanges || hasNameChange);
    }, [areas, layoutName.name, newAreas, selectedLayout.title]);

    useEffect(() => {
        if (unit.value == "px") {
            // convertToPx()//convert area to px
            const areasConverted = convertToPx(areas, currentAspectRatio);
            setAreas(areasConverted)
        }
        if (unit.value == "%") {
            // handleSwitchUnit()//convert area to % 
            const areasConverted = handleSwitchUnit(areas, currentAspectRatio);
            setAreas(areasConverted)
        }
    }, [unit])

    const handleEditTemplate = () => {
        const areasConverted = handleSwitchUnit(areas, currentAspectRatio);
        const sanitizedAreas = areasConverted.map(({ unit, ...rest }) => rest);

        const screenIds = screens_setting.map(item => item.id).sort();
        const areaIds = sanitizedAreas
            .filter(item => item.id != null)
            .map(item => item.id)
            .sort(); const hasSameIds = screenIds.every((id, index) => id === areaIds[index]);

        if (areas.length < oldLayout.length) {
            setOldLayout(selectedLayout.screens_setting)
            setNewLayout(sanitizedAreas)
            // setIsConflictScreens(true)
            dispatch(toggleDialog("layoutConflict"))

        } else if (!hasSameIds) {
            setOldLayout(selectedLayout.screens_setting)
            setNewLayout(sanitizedAreas)
            // setIsConflictScreens(true)
            dispatch(toggleDialog("layoutConflict"))


        } else {

            setIsConflictScreens(false)
            setNewLayout(sanitizedAreas)
            handleChange();
        }
    };

    const handleChange = () => {
        const areasConverted = handleSwitchUnit(areas, currentAspectRatio);
        const areaPercentage = areasConverted.map(({ unit, ...rest }) => rest);

        const toSethScreenSetting = areaPercentage.map(({ id, name, width, height, start_margin, top_margin, settings }, index) => ({
            id: id,
            name,
            width,
            height,
            start_margin,
            top_margin,
            order: index + 1,
            settings: {
                ...settings,
                live: {
                    ...(settings?.live || {}),
                    order: index + 1
                }
            }
        }));

        const sanitizedAreas = areaPercentage
            .filter(({ id }) => id != null)
            .map(({ id, name, width, height, start_margin, top_margin, settings }, index) => ({
                id,
                name,
                width,
                height,
                start_margin,
                top_margin,
                order: index + 1,
                settings: {
                    ...settings,
                    live: {
                        ...(settings?.live || {}),
                        order: index + 1
                    }
                }
            }));
        dispatch(setScreenDetails({}))
        console.log("Areas to be sent to backend (Update):", toSethScreenSetting);
        updateLayout(
            {
                layoutId: id,
                data: {
                    title: layoutName.name,
                    screens_setting: toSethScreenSetting,
                    type: "CUSTOM",
                    status: "ACTIVE",
                    aspect_ratio_id: currentAspectRatio.id,
                    updated_campaigns: sanitizedAreas
                },
            },
            {
                onSuccess: () => {
                    setHasUnsavedChanges(false);
                    dispatch(toggleDialog("EditLayoutPopUp"));

                },
            }
        );
    };

    const handleUnitChange = (unitValue) => {
        dispatch(setUnit({ value: unitValue }));
    };
    
    return (
        <div className={`flex flex-col h-screen overflow-hidden font-inter transition-colors duration-300 ${isDarkMode ? 'bg-slate-950 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
            {/* Studio Professional Header */}
            <div className={`layout-editor-header h-14 border-b px-4 flex items-center justify-between shrink-0 z-40 sticky top-0 shadow-sm relative transition-colors duration-300 ${
                isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'
            }`}>
                 {/* Left: Context & Navigation */}
                 <div className="flex items-center gap-3 flex-1 w-1/3">
                     <div className="flex items-center gap-2 text-sm">
                         <span className={`font-medium transition-colors cursor-pointer ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{t('layouts.setup_breadcrumb_apps')}</span>
                         <i className={`bx bx-chevron-right text-base ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                         <span className={`font-medium transition-colors cursor-pointer ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{t('layouts.setup_breadcrumb_layouts')}</span>
                         <i className={`bx bx-chevron-right text-base ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                         
                         <div className={`flex items-center gap-2 group cursor-default ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            <div className={`w-6 h-6 rounded flex items-center justify-center text-white shadow-sm ${isDarkMode ? 'bg-indigo-500' : 'bg-gray-900'}`}>
                                <i className="bx bxs-layout text-xs"></i>
                            </div>
                            <span className="text-sm font-semibold tracking-wide">{t('layouts.setup_layout_editor')}</span>
                         </div>

                         <i className={`bx bx-chevron-right text-base ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                         <span className={`font-semibold truncate max-w-[150px] ${isDarkMode ? 'text-indigo-400' : 'text-gray-600'}`} title={layoutName.name}>{layoutName.name}</span>
                     </div>
                     
                     <div className={`h-4 w-px mx-2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>

                     {/* Mode Switcher - Standard Bordered Dropdown */}
                     <div className="h-8">
                        <Dropdown 
                             value={activeIndex} 
                             options={navItems} 
                             optionLabel="label" 
                             optionValue="value"
                             onChange={(e) => setActiveIndex(e.value)} 
                             className="h-full items-center w-48 border border-gray-300 shadow-sm rounded-md bg-white hover:border-gray-400 transition-colors" 
                             pt={{
                                 input: { className: "text-xs font-medium text-gray-700 p-2" },
                                 trigger: { className: "w-8 text-gray-500" },
                                 panel: { className: "text-sm shadow-lg border border-gray-200 rounded-md mt-1" },
                                 item: { className: "py-2 px-3 hover:bg-gray-50 text-xs text-gray-700 cursor-pointer" }
                             }}
                        />
                     </div>
                 </div>

                 {/* Center: Layout Title - Standard Input Look */}
                 <div className="flex-1 shrink-0 flex justify-center w-1/3">
                      <div className="w-full max-w-xs">
                           <LayoutNameController 
                                layoutName={layoutName} 
                                setLayoutName={setLayoutName} 
                                titleError={titleError} 
                           />
                      </div>
                 </div>

                 {/* Right: Tools & Actions */}
                 <div className="flex items-center justify-end gap-3 flex-1 w-1/3">
                     {/* Unit Segmented Control */}
                     <div className="flex items-center gap-2">
                         <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('layouts.setup_unit')}</span>
                         <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200 h-8">
                             {unitOptions.map((opt) => (
                                 <button
                                    key={opt.value}
                                    onClick={() => handleUnitChange(opt.value)}
                                    className={`px-3 text-xs font-medium rounded-md transition-all ${
                                        (unit?.value || unitOptions[0].value) === opt.value 
                                        ? "bg-white text-gray-900 shadow-sm border border-gray-200" 
                                        : "text-gray-500 hover:text-gray-700"
                                    }`}
                                 >
                                     {opt.label}
                                 </button>
                             ))}
                         </div>
                     </div>
                     
                     <div className={`h-4 w-px mx-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>

                     {/* Dark Mode Toggle */}
                     <button
                        onClick={toggleDarkMode}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 border ${
                            isDarkMode 
                            ? "bg-slate-800 text-yellow-400 border-slate-700 hover:bg-slate-700 hover:text-yellow-300" 
                            : "bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200 hover:text-gray-700"
                        }`}
                        title={isDarkMode ? t('layouts.setup_switch_light') : t('layouts.setup_switch_dark')}
                     >
                        <i className={`bx ${isDarkMode ? 'bx-sun' : 'bx-moon'} text-lg`}></i>
                     </button>

                     <div className={`h-4 w-px mx-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>

                     {/* Sidebar Pin Toggle */}
                     <button 
                        onClick={toggleSidebarPin}
                        className={`h-8 flex items-center gap-2 px-3 rounded-lg transition-all duration-200 border text-xs font-semibold ${
                            isSidebarPinned 
                            ? "bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100" 
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-transparent"
                        }`}
                        title={isSidebarPinned ? t('layouts.setup_unpin_sidebar') : t('layouts.setup_pin_sidebar')}
                     >
                        <i className={`bx ${isSidebarPinned ? 'bx-pin' : 'bx-pin'} text-sm`}></i>
                        <span>{isSidebarPinned ? t('layouts.setup_pinned_sidebar') : t('layouts.setup_pin_sidebar')}</span>
                     </button>

                     {/* Preview Button */}
                     <button 
                        onClick={() => setShowPreview(true)}
                        className="h-8 flex items-center gap-2 px-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold text-xs shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 transition-all duration-300 hover:scale-105 border border-indigo-500/50"
                        title={t('layouts.setup_preview_layout_title')}
                     >
                        <i className="bx bx-show text-sm"></i>
                        <span>{t('buttons.preview')}</span>
                     </button>

                     <div className={`h-4 w-px mx-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>

                     <button 
                        onClick={handleCloseAttempt}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200 border border-transparent hover:border-red-100"
                        title={t('layouts.setup_close_editor')}
                     >
                        <i className="bx bx-x text-xl"></i>
                     </button>
                 </div>
            </div>

            {/* Main Workspace */}
            <div className="flex flex-1 overflow-hidden relative">
                 {/* Sidebar Strip (Visible when Unpinned) */}
                 {!isSidebarPinned && (
                    <div id="sidebar-strip" className={`absolute right-0 top-0 bottom-0 w-12 border-l z-30 flex flex-col items-center py-4 gap-4 shadow-sm transition-colors duration-300 ${
                        isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'
                    }`}>
                        <button 
                            onClick={() => handleSidebarTabClick('areas')}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                (isSidebarOpenUnpinned && sidebarTab === 'areas') 
                                ? 'bg-indigo-50 text-indigo-600' 
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                            }`}
                            title={t('layouts.setup_areas')}
                        >
                            <i className="bx bx-grid-alt text-xl"></i>
                        </button>
                        <div className={`w-8 h-px my-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>

                        {/* Individual Tool Icons */}
                        {[
                            { id: 'view', label: 'View', icon: 'bx-show' },
                            { id: 'grid', label: 'Grid', icon: 'bx-grid-alt' },
                            { id: 'measure', label: 'Measure', icon: 'bx-ruler' },
                            { id: 'style', label: 'Borders', icon: 'bx-border-all' },
                            { id: 'colors', label: 'Colors', icon: 'bx-palette' }
                        ].map(tool => (
                            <button 
                                key={tool.id}
                                onClick={() => {
                                    setActiveTab(tool.id);
                                    handleSidebarTabClick('tools');
                                }}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                    (isSidebarOpenUnpinned && sidebarTab === 'tools' && toolsState?.activeTab === tool.id) 
                                    ? 'bg-purple-500/20 text-purple-400' 
                                    : (isDarkMode ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700')
                                }`}
                                title={tool.label}
                            >
                                <i className={`bx ${tool.icon} text-xl`}></i>
                            </button>
                        ))}
                    </div>
                 )}

                 {/* Canvas Area */}
                 <div 
                    className={`flex-1 overflow-hidden relative flex flex-col transition-all ease-out ${isResizing ? '' : 'duration-300'} ${
                        isDarkMode ? 'bg-slate-950' : 'bg-gray-50/50'
                    }`}
                    style={{
                        paddingRight: !isSidebarPinned 
                            ? (isSidebarOpenUnpinned ? `${sidebarWidth + 48}px` : '3rem') 
                            : 0
                    }}
                  > 
                      <div 
                          className={`flex-1 overflow-auto flex items-center justify-center custom-scrollbar relative transition-all duration-300 ${
                              isCanvasFullscreen ? 'p-0' : 'p-8'
                          }`} 
                          ref={scrollableDivRef}
                      >
                          
                          {/* Dot Pattern Background */}
                          <div className="absolute inset-0 z-0 opacity-[0.4] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
                          
                          {/* Zoomable Wrapper */}
                          <div 
                              className="relative z-10 w-full h-full flex items-center justify-center transition-transform duration-200 ease-out origin-center"
                          >
                            <DynamicContainer
                                isMaximized={isMaximized}
                                isCanvasFullscreen={isCanvasFullscreen}
                                areas={areas}
                                setAreas={setAreas}
                                layoutColor={layoutColor}
                                currentAspectRatio={currentAspectRatio}
                                setCurrentAspectRatio={setCurrentAspectRatio}
                                scrollableDivRef={scrollableDivRef}
                                zoomLevel={zoomLevel}
                                toolsState={toolsState}
                                isPaused={showPreview}
                                setAreaColor={setAreaColor}
                                setAreaOpacity={setAreaOpacity}
                            />
                          </div>
                      </div>

                      {/* Zoom Controls Floating */}
                      <div className={`absolute bottom-36 left-6 flex flex-col gap-2 z-50 p-1.5 rounded-xl shadow-lg border transition-colors duration-300 ${
                          isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-100'
                      }`}>
                          {/* Fullscreen Toggle */}
                          <button 
                              onClick={toggleCanvasFullscreen} 
                              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                                  isDarkMode 
                                      ? 'hover:bg-slate-700 text-gray-400 hover:text-gray-200' 
                                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                              } ${isCanvasFullscreen ? (isDarkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-50 text-indigo-600') : ''}`}
                              title={isCanvasFullscreen ? t('layouts.setup_exit_fullscreen') : t('layouts.setup_fullscreen_canvas')}
                          >
                              <i className={`bx ${isCanvasFullscreen ? 'bx-exit-fullscreen' : 'bx-fullscreen'} text-xl`}></i>
                          </button>
                          
                          {/* Separator */}
                          <div className={`h-px ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                          
                          <button onClick={handleZoomIn} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                              isDarkMode ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                          }`} title={t('layouts.setup_zoom_in')}>
                              <i className="bx bx-plus text-xl"></i>
                          </button>
                           <button onClick={handleResetZoom} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors font-bold text-xs ${
                               isDarkMode ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                           }`} title={t('layouts.setup_reset')}>
                              {Math.round(zoomLevel * 100)}%
                          </button>
                          <button onClick={handleZoomOut} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                              isDarkMode ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                          }`} title={t('layouts.setup_zoom_out')}>
                              <i className="bx bx-minus text-xl"></i>
                          </button>
                      </div>
                      
                      <AreaSelectorBar 
                          areas={areas}
                          toolsState={toolsState}
                          isDarkMode={isDarkMode}
                      />
                 </div>

                 {/* Resize Handle */}
                 {(isSidebarPinned || isSidebarOpenUnpinned) && (
                     <div
                        className={`w-1 hover:w-1.5 bg-gray-200 hover:bg-indigo-400 cursor-ew-resize transition-all z-50 flex items-center justify-center group shrink-0 
                            ${isResizing ? 'bg-indigo-500 w-1.5' : ''}
                            ${!isSidebarPinned ? 'absolute top-0 bottom-0' : 'relative'}
                        `}
                        style={{
                            right: !isSidebarPinned ? `${sidebarWidth + 48}px` : 'auto' // 48px = strip width (w-12)
                        }}
                        onMouseDown={startResizing}
                     >
                        <div className={`h-8 w-1 rounded-full group-hover:bg-white transition-colors absolute ${isResizing ? 'bg-white' : 'bg-gray-400'}`} />
                     </div>
                 )}

                 {/* Inspector Panel (Right Sidebar) */}
                 <div 
                    id="layout-editor-sidebar"
                    className={`border-l shadow-xl z-40 flex flex-col h-full transition-all ease-out ${
                        isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'
                    } 
                        ${!isSidebarPinned ? 'absolute right-12 top-0 bottom-0 shadow-2xl border-l-0' : 'relative'}
                        ${isResizing ? '' : 'duration-300'}
                    `}
                    style={{ 
                        width: `${sidebarWidth}px`,
                        transform: !isSidebarPinned && !isSidebarOpenUnpinned ? 'translateX(100%)' : 'translateX(0)',
                        opacity: !isSidebarPinned && !isSidebarOpenUnpinned ? 0 : 1,
                        visibility: !isSidebarPinned && !isSidebarOpenUnpinned ? 'hidden' : 'visible'
                    }}
                 >
                      <div className={`p-4 border-b sticky top-0 z-10 backdrop-blur-md shrink-0 flex items-center justify-between transition-colors ${
                          isDarkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white/95 border-gray-100'
                      }`}>
                          <h2 className={`font-semibold flex items-center gap-2 text-sm uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              <i className={`bx ${sidebarTab === 'areas' ? 'bx-grid-alt text-indigo-500' : 'bx-wrench text-purple-500'} text-lg`}></i> 
                              {sidebarTab === 'areas' ? t('layouts.setup_area_manager') : t('layouts.setup_design_tools')}
                          </h2>
                          
                          {/* Close button for unpinned mode */}
                          {!isSidebarPinned && (
                              <button onClick={() => setIsSidebarOpenUnpinned(false)} className="text-gray-400 hover:text-gray-600">
                                  <i className="bx bx-x text-xl"></i>
                              </button>
                          )}
                      </div>
                      
                      {/* Tabs (Only Show in Pinned Mode - In Unpinned, Strip handles updated) */}
                      {isSidebarPinned && (
                        <div className="px-4 pb-2">
                            <div className={`flex gap-2 p-1 rounded-lg border transition-colors ${
                                isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'
                            }`}>
                                <button
                                    onClick={() => setSidebarTab('areas')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md transition-all text-xs font-semibold ${
                                        sidebarTab === 'areas'
                                            ? (isDarkMode ? 'bg-slate-800 text-indigo-400 shadow-sm border border-slate-700' : 'bg-white text-indigo-600 shadow-sm border border-gray-200')
                                            : (isDarkMode ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-900' : 'text-gray-600 hover:bg-white/50')
                                    }`}
                                >
                                    <i className="bx bx-grid-alt text-sm"></i>
                                    <span>{t('layouts.setup_areas')}</span>
                                </button>
                                <button
                                    onClick={() => setSidebarTab('tools')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md transition-all text-xs font-semibold ${
                                        sidebarTab === 'tools'
                                            ? (isDarkMode ? 'bg-slate-800 text-purple-400 shadow-sm border border-slate-700' : 'bg-white text-purple-600 shadow-sm border border-gray-200')
                                            : (isDarkMode ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-900' : 'text-gray-600 hover:bg-white/50')
                                    }`}
                                >
                                    <i className="bx bx-wrench text-sm"></i>
                                    <span>{t('layouts.setup_tools')}</span>
                                </button>
                            </div>
                        </div>
                      )}


                      
                      <div className={`flex-1 overflow-y-auto p-4 custom-scrollbar ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                          {sidebarTab === 'areas' ? (
                              <>
                                  <AreaControls
                                      layoutColor={layoutColor}
                                      setIsConflictScreens={setIsConflictScreens}
                                      areas={areas}
                                      setAreas={setAreas}
                                      currentAspectRatio={currentAspectRatio}
                                      isSaveDisabled={isSaveDisabled}
                                      overlappingAreas={overlappingAreas}
                                      setOverlappingAreas={setOverlappingAreas}
                                      setIsSaveDisabled={setIsSaveDisabled}
                                      scrollableDivRef={scrollableDivRef}
                                      isDarkMode={isDarkMode}
                                      sidebarWidth={sidebarWidth}
                                      toolsState={toolsState}
                                  />


                              </>
                          ) : (
                              <DesignToolsPanel
                                  isOpen={true}
                                  onClose={() => setSidebarTab('areas')}
                                  toolsState={toolsState}
                                  updateTool={updateTool}
                                  toggleTool={toggleTool}
                                  setAreaColor={setAreaColor}
                                  setAreaOpacity={setAreaOpacity}
                                  showToolTutorial={showToolTutorial}
                                  areas={areas}
                                  embedded={true}
                                  activeTab={toolsState?.activeTab || 'view'}
                                  setActiveTab={setActiveTab}
                              />
                          )}
                      </div>

                      {/* Footer Actions */}
                      <div className={`p-4 border-t flex gap-3 sticky bottom-0 z-10 shrink-0 transition-colors ${
                          isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-gray-50 border-gray-200'
                      }`}>
                          <Button 
                            label={t('layouts.setup_reset')}
                            className="flex-1 py-3 bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 hover:text-gray-800 rounded-lg shadow-sm transition-all text-sm font-semibold"
                            onClick={() => handleCancel()}
                          />
                          <Button
                            label={t('buttons.save')}
                            className="flex-1 py-3 bg-indigo-600 border-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-200 rounded-lg text-white transition-all text-sm font-bold tracking-wide"
                            disabled={isSaveDisabled || areas?.length == 0}
                            onClick={() => handleEditTemplate()}
                          />
                      </div>
                 </div>
            </div>

            {/* Tool Tutorial Modal */}
            <ToolTutorialModal
                visible={showTutorial}
                onHide={hideTutorial}
                toolName={selectedTool}
            />

            {openDialog.layoutConflict && <LayoutConflictWarning setAreas={setAreas} newAreas={newAreas} currentAspectRatio={currentAspectRatio} custom={true} isLoading={isLoading} oldLayout={oldLayout} setOldLayout={setOldLayout} handleChange={handleChange} newLayout={newLayout} oldAspectRatio={oldAspectRatio} />}

            {/* Unsaved Changes Warning Modal */}
            {showUnsavedWarning && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in duration-300">
                        {/* Gradient Header */}
                        <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-violet-600 p-6 pb-8">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                            
                            <div className="relative flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border-2 border-white/30 shadow-lg">
                                    <i className="bx bx-save text-3xl text-white drop-shadow-md"></i>
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-white drop-shadow-md">{t('messages.unsaved_changes')}</h3>
                                    <p className="text-white/90 text-sm mt-0.5">{t('layouts.setup_unsaved_modifications')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 -mt-4">
                            <div className="bg-indigo-50/80 rounded-xl p-4 border border-indigo-100 mb-6">
                                <p className="text-gray-700 leading-relaxed">
                                    {t('layouts.setup_unsaved_body')}
                                    <span className="font-semibold text-indigo-700"> {t('layouts.setup_unsaved_warning')}</span>
                                </p>
                            </div>

                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-gray-600 mb-3">{t('layouts.setup_what_to_do')}</p>
                                
                                {/* Action Buttons */}
                                <div className="grid grid-cols-2 gap-3">
                                    {/* Save & Close */}
                                    <button
                                        onClick={() => {
                                            setShowUnsavedWarning(false);
                                            handleChange();
                                        }}
                                        className="group relative overflow-hidden px-4 py-3 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 transition-all duration-300 hover:scale-105"
                                    >
                                        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                                        <div className="relative flex items-center justify-center gap-2">
                                            <i className="bx bx-save text-lg"></i>
                                            <span>{t('layouts.setup_save_and_close')}</span>
                                        </div>
                                    </button>

                                    {/* Discard */}
                                    <button
                                        onClick={handleDiscardChanges}
                                        className="group relative overflow-hidden px-4 py-3 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 text-white font-semibold shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 transition-all duration-300 hover:scale-105"
                                    >
                                        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                                        <div className="relative flex items-center justify-center gap-2">
                                            <i className="bx bx-trash text-lg"></i>
                                            <span>{t('layouts.setup_discard')}</span>
                                        </div>
                                    </button>
                                </div>

                                {/* Cancel */}
                                <button
                                    onClick={() => setShowUnsavedWarning(false)}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 hover:bg-indigo-50 text-gray-600 hover:text-indigo-700 font-semibold transition-all duration-200 border border-gray-200 hover:border-indigo-200"
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <i className="bx bx-arrow-back text-lg"></i>
                                        <span>{t('layouts.setup_continue_editing')}</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Layout Modal */}
            {showPreview && (
                <PreviewLayoutModal 
                    onClose={() => setShowPreview(false)} 
                    layoutName={layoutName.name}
                    screens_setting={screens_setting}
                />
            )}
        </div>
    )
}



export default EditLayout